import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { setPageJsonLd, removePageJsonLd } from '../../utils/jsonLd';

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    document.title = 'Off-Grid Products & Camping Gear | Shocker Solutions';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Browse our curated selection of tested solar generators, off-grid refrigeration, and camping showers. Jackery, SetPower, and quality outdoor equipment.');
    }
    
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://www.shockersolutions.com/products');
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.shockersolutions.com';

    setPageJsonLd({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${siteUrl}/products`,
      url: `${siteUrl}/products`,
      name: 'Off-Grid Products & Camping Gear | Shocker Solutions',
      description: 'Curated selection of tested solar generators, off-grid refrigeration, and camping showers.',
      numberOfItems: 9,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Jackery HomePower 3000', url: 'https://www.jackery.com' },
        { '@type': 'ListItem', position: 2, name: 'Jackery Explorer 1000 Plus', url: 'https://www.jackery.com' },
        { '@type': 'ListItem', position: 3, name: 'SetPower AJ40 Portable Fridge', url: 'https://www.setpowerusa.com/' },
        { '@type': 'ListItem', position: 4, name: 'SetPower RV Refrigerator 55L', url: 'https://www.setpowerusa.com/' },
        { '@type': 'ListItem', position: 5, name: 'Camplux Nano 3 Water Heater', url: 'https://amzn.to/47rxgca' },
        { '@type': 'ListItem', position: 6, name: 'Propane Camping Shower System', url: 'https://www.amazon.com' },
        { '@type': 'ListItem', position: 7, name: 'Solar Camping Shower Bag 5 Gallon', url: 'https://www.amazon.com' },
      ],
    });

    return () => removePageJsonLd();
  }, []);

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'solar', name: 'Solar Generators' },
    { id: 'refrigeration', name: 'Refrigeration' },
    { id: 'showers', name: 'Hot Showers' },
  ];

  const products = [
    {
      id: 1,
      category: 'solar',
      name: 'Jackery HomePower 3000',
      price: '$1,999',
      rating: 5,
      image: 'https://static.readdy.ai/image/4cab5009fae4c10096ff71d938618757/7739242d1dc8af87bb54ee389d4e2bc0.png',
      features: ['2160Wh Capacity', 'Fast Charging', 'Solar Ready'],
      link: 'https://www.jackery.com',
    },
    {
      id: 2,
      category: 'solar',
      name: 'Jackery Explorer 1000 Plus',
      price: '$1,299',
      rating: 5,
      image: 'https://public.readdy.ai/ai/img_res/10b182bdd8807ec6c747b7e6714bd8fe.jpg',
      features: ['1264Wh Capacity', 'Expandable', 'App Control'],
      link: 'https://www.jackery.com',
    },
    {
      id: 3,
      category: 'solar',
      name: 'Jackery Explorer 500',
      price: '$599',
      rating: 4,
      image: 'https://readdy.ai/api/search-image?query=Jackery%20Explorer%20500%20compact%20solar%20generator%20product%20shot%20on%20white%20background%20ecommerce%20photography%20style%20orange%20branding%20clean%20professional%20image&width=500&height=500&seq=prod-jackery-500-001&orientation=squarish',
      features: ['518Wh Capacity', 'Lightweight', 'Portable'],
      link: 'https://www.jackery.com',
    },
    {
      id: 4,
      category: 'refrigeration',
      name: 'SetPower AJ40 Portable Fridge',
      price: '$449',
      rating: 5,
      image: 'https://readdy.ai/api/search-image?query=SetPower%20portable%20electric%20cooler%20refrigerator%20product%20photography%20on%20clean%20white%20background%20ecommerce%20style%20with%20digital%20display%20professional%20lighting%20orange%20accents&width=500&height=500&seq=prod-setpower-aj40-001&orientation=squarish',
      features: ['40L Capacity', 'Dual Zone', '-4°F to 68°F'],
      link: 'https://www.setpowerusa.com/',
    },
    {
      id: 5,
      category: 'refrigeration',
      name: 'SetPower RV Refrigerator 55L',
      price: '$599',
      rating: 4,
      image: 'https://readdy.ai/api/search-image?query=SetPower%20large%20portable%20refrigerator%2055%20liter%20product%20shot%20on%20white%20background%20ecommerce%20photography%20professional%20style%20with%20orange%20color%20details&width=500&height=500&seq=prod-setpower-55l-001&orientation=squarish',
      features: ['55L Capacity', 'Energy Efficient', 'Low Noise'],
      link: 'https://www.setpowerusa.com/',
    },
    {
      id: 6,
      category: 'refrigeration',
      name: 'SetPower FC25 Car Fridge',
      price: '$329',
      rating: 4,
      image: 'https://readdy.ai/api/search-image?query=SetPower%20compact%20car%20refrigerator%2025%20liter%20product%20photography%20on%20clean%20white%20background%20ecommerce%20style%20professional%20lighting%20orange%20branding&width=500&height=500&seq=prod-setpower-fc25-001&orientation=squarish',
      features: ['25L Capacity', 'Compact Design', '12V/24V'],
      link: 'https://www.setpowerusa.com/',
    },
    {
      id: 7,
      category: 'showers',
      name: 'Propane Camping Shower System',
      price: '$199',
      rating: 5,
      image: 'https://readdy.ai/api/search-image?query=portable%20propane%20camping%20shower%20water%20heater%20system%20product%20shot%20on%20white%20background%20ecommerce%20photography%20professional%20style%20with%20orange%20accents&width=500&height=500&seq=prod-propane-shower-001&orientation=squarish',
      features: ['Instant Hot Water', 'Propane Powered', 'Adjustable Temp'],
      link: 'https://www.amazon.com',
    },
    {
      id: 8,
      category: 'showers',
      name: 'Solar Camping Shower Bag 5 Gallon',
      price: '$39',
      rating: 4,
      image: 'https://readdy.ai/api/search-image?query=solar%20camping%20shower%20bag%20black%20with%20hose%20and%20nozzle%20product%20photography%20on%20white%20background%20ecommerce%20style%20professional%20lighting%20orange%20details&width=500&height=500&seq=prod-solar-bag-001&orientation=squarish',
      features: ['5 Gallon Capacity', 'Solar Heated', 'Eco-Friendly'],
      link: 'https://www.amazon.com',
    },
    {
      id: 9,
      category: 'showers',
      name: 'Portable Electric Camping Shower',
      price: '$89',
      rating: 4,
      image: 'https://readdy.ai/api/search-image?query=portable%20electric%20camping%20shower%20pump%20with%20bucket%20product%20shot%20on%20white%20background%20ecommerce%20photography%20professional%20style%20orange%20color%20accents&width=500&height=500&seq=prod-electric-shower-001&orientation=squarish',
      features: ['USB Rechargeable', 'Portable Pump', 'Versatile Use'],
      link: 'https://www.amazon.com',
    },
  ];

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-6xl font-bold text-gray-900 mb-4">Our Products</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every product listed here has been personally tested and approved by our team.
            </p>
          </div>

          <div className="flex justify-center gap-3 mb-12 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-orange-500 text-black shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {filteredProducts.map((product) => (
              <article key={product.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
                <div className="relative h-64 w-full bg-gray-50 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    title={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-sm ${i < product.rating ? 'text-orange-500' : 'text-gray-300'}`}
                      ></i>
                    ))}
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-2 min-h-[3rem]">
                    {product.name}
                  </h2>

                  <div className="text-2xl font-bold text-orange-500 mb-4">{product.price}</div>

                  <ul className="space-y-1 mb-6">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-orange-500"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <a
                    href={product.link}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="block w-full text-center bg-orange-500 hover:bg-orange-600 text-black font-semibold px-6 py-3 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    View Details
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
